"use client";

import { cn } from "@jesus-central-church/shared";
import { motion } from "motion/react";
import { Link } from "../link";
import { Image } from "../media/image";
import { SocialLink } from "./cinematic-menu";

export interface FooterLogo {
  filename?: string;
  alt?: string;
  width?: number;
  height?: number;
}

export interface FooterMenuSection {
  _uid: string;
  title?: string;
  items: FooterMenuItem[];
}

export interface FooterMenuItem {
  _uid: string;
  label?: string;
  href?: string;
}

export interface FooterProperties extends Record<string, unknown> {
  locale: string;
  logo?: FooterLogo;
  description?: string;
  tagline?: string;
  menuSections?: FooterMenuSection[];
  socialLinks?: SocialLink[];
  languageSelector?: React.ReactNode;
  className?: string;
}

export function Footer({
  locale,
  logo,
  description,
  tagline,
  menuSections,
  socialLinks,
  languageSelector,
  className,
  ...additionalProperties
}: FooterProperties) {
  return (
    <footer
      className={cn(
        "bg-boldness text-freedom relative overflow-hidden",
        className,
      )}
      {...additionalProperties}
    >
      {/* Subtle animated background */}
      <div className="absolute inset-0 opacity-5">
        <motion.div
          className="from-strategy-gold/20 to-strategy-gold/20 absolute inset-0 bg-gradient-to-br via-transparent"
          animate={{
            scale: [1, 1.05, 1],
            rotate: [0, 2, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </div>

      {/* Main Footer Content */}
      <div className="relative">
        {/* Top accent line */}
        <div className="via-strategy-gold/50 h-[1px] bg-gradient-to-r from-transparent to-transparent" />

        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-8">
          {/* Logo & Tagline Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 lg:mb-16"
          >
            <div className="mx-auto flex max-w-md flex-col items-center text-center lg:mx-0 lg:items-start lg:text-left">
              <Link href={`/${locale}/`} className="mb-4 block">
                <Image
                  src={logo?.filename || "/logo.svg"}
                  alt={logo?.alt || "Jesus Central Church"}
                  width={logo?.width ?? 200}
                  height={logo?.height ?? 60}
                  className="h-12 w-auto sm:h-14 md:h-16"
                />
              </Link>
              {description && (
                <p className="text-freedom/70 text-sm leading-relaxed sm:text-base">
                  {description}
                </p>
              )}
            </div>
          </motion.div>

          {/* Links Grid */}
          {menuSections && menuSections.length > 0 && (
            <div className="mb-12 grid grid-cols-2 gap-8 lg:mb-16 lg:grid-cols-4">
              {menuSections.map((section, sectionIndex) => (
                <motion.div
                  key={section._uid}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
                >
                  {section.title && (
                    <h3 className="text-strategy-gold mb-4 text-xs font-bold tracking-wider uppercase">
                      {section.title}
                    </h3>
                  )}
                  <ul className="space-y-3">
                    {section.items.map((link) => (
                      <li key={link._uid}>
                        <Link
                          href={link.href || "#"}
                          className="text-freedom/70 hover:text-strategy-gold group inline-block text-sm transition-colors duration-300"
                        >
                          <span className="relative">
                            {link.label}
                            <span className="bg-strategy-gold absolute bottom-0 left-0 h-[1px] w-0 transition-all duration-300 group-hover:w-full" />
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          )}

          {/* Bottom Section: Social, Language & Copyright */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="border-freedom/10 flex flex-col items-center space-y-6 border-t pt-8"
          >
            {/* Social Links & Language Selector Row */}
            <div className="flex w-full flex-col items-center justify-between gap-6 sm:flex-row">
              {/* Social Links */}
              {socialLinks && socialLinks.length > 0 && (
                <div className="flex items-center gap-4">
                  <span className="text-freedom/50 mr-2 text-xs tracking-wider uppercase">
                    Volg Ons
                  </span>
                  {socialLinks.map((social) => (
                    <Link
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "flex h-10 w-10 items-center justify-center rounded-full",
                        "bg-freedom/5 text-freedom/70 hover:bg-strategy-gold hover:text-freedom",
                        "transition-all duration-300 hover:scale-110",
                        "border-freedom/10 hover:border-strategy-gold border",
                      )}
                      aria-label={social.label}
                    >
                      {social.icon}
                    </Link>
                  ))}
                </div>
              )}

              {/* Language Selector */}
              {languageSelector && (
                <div className="flex items-center gap-2">
                  {languageSelector}
                </div>
              )}
            </div>

            {/* Copyright & Legal */}
            <div className="text-freedom/50 flex flex-col items-center gap-4 text-xs sm:flex-row">
              <p>© {new Date().getFullYear()} <Link href="https://jesuscentral.church" target="_blank" rel="noopener noreferrer">Jesus Central Church</Link></p>
              <span className="hidden sm:inline">•</span>
              <div className="flex items-center gap-4">
                <Link
                  href={`/${locale}/privacy`}
                  className="hover:text-strategy-gold transition-colors duration-300"
                >
                  Privacy
                </Link>
              </div>
            </div>

            {/* Subtle tagline */}
            {tagline && (
              <p className="text-freedom/40 text-xs italic">{tagline}</p>
            )}
          </motion.div>
        </div>
      </div>

      {/* Bottom accent line with animation */}
      <motion.div
        className="via-strategy-gold h-[2px] bg-gradient-to-r from-transparent to-transparent"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, ease: "easeInOut" }}
      />
    </footer>
  );
}
